import React, { FC, Suspense, useState } from 'react'
import { Button, Loading, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  ResidentAppAsideText,
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { MessageTimeline } from './MessageTimeline'
import { MessageFormContainer } from './MessageForm'
import { MessagesViewDataLoader } from './MessagesViewDataLoader'

import { ResidentNotifications } from 'components'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { NotFound } from 'views/NotFound'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useAPIGetUserMetadataSuspense } from 'api/apiHooks/useAPIGetUserMetadata'

export const Messages: FC = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['common', 'messages', 'titles'])
  const { frontPagePath, messageThreadPath } = useContractPaths()
  const { messaging, extendedMessaging } = useModuleConfig()
  const { data: userMetadata } = useAPIGetUserMetadataSuspense()

  const navi = useNavigate()

  const [showForm, setShowForm] = useState(false)
  const { messageThreadUuid } = useParams() as { messageThreadUuid?: string }

  const [formState, setFormState] = useState<{
    messageThreadUuid?: string
    replySubject?: string
    focusSubject: boolean
  }>({ focusSubject: false, messageThreadUuid })

  const handleCreateNewMessage = () => {
    setFormState({
      messageThreadUuid: undefined,
      replySubject: undefined,
      focusSubject: true,
    })
    setShowForm(true)
  }

  const handleToggleForm = () => {
    if (showForm) {
      setShowForm(false)
      setFormState({ focusSubject: false })
    } else {
      handleCreateNewMessage()
    }
  }

  if (!messaging) {
    return <NotFound />
  }

  return (
    <TitleController title={t('titles:messages')}>
      <ResidentAppView
        title={
          showForm
            ? t('messages:view.sendNewMessage')
            : t('messages:view.title')
        }
        backLink={
          showForm ? (
            <ResidentAppBackLink onClick={handleToggleForm}>
              {t('messages:view.title')}
            </ResidentAppBackLink>
          ) : (
            <ResidentAppBackLink href={frontPagePath}>
              {t('common:views.home')}
            </ResidentAppBackLink>
          )
        }
        aside={
          <ResidentAppAsideText>
            {t('messages:view.description')}
          </ResidentAppAsideText>
        }
      >
        <ResidentAppCard>
          {/* Hidden notifications component to handle closing message notifications */}
          <div className="hidden">
            <ResidentNotifications />
          </div>

          {extendedMessaging && (
            <>
              {showForm ? (
                <MessageFormContainer
                  key={formState.messageThreadUuid ?? 'new'}
                  threadId={formState.messageThreadUuid}
                  replySubject={formState.replySubject}
                  focusSubject={formState.focusSubject}
                  showParticipantsNotice={false}
                  onCancel={() => setShowForm(false)}
                  onSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: ['resident-message-timeline'],
                    })
                  }}
                />
              ) : (
                <div className="flex justify-end mb-5">
                  <Button
                    onClick={handleToggleForm}
                    disabled={userMetadata.isImpersonator}
                  >
                    {t('messages:view.sendNewMessage')}
                  </Button>
                </div>
              )}
            </>
          )}

          {!showForm && (
            <Suspense fallback={<Loading variant="details" />}>
              <MessagesViewDataLoader>
                {(messages) =>
                  messages.length === 0 ? (
                    <Alert
                      variant="info"
                      className="my-8"
                      data-testid="empty-message-alert"
                    >
                      {t('messages:view.empty')}
                    </Alert>
                  ) : (
                    <MessageTimeline
                      messages={messages}
                      onViewThread={(id: string) => {
                        if (userMetadata.isImpersonator) return
                        setFormState({
                          messageThreadUuid: id,
                          focusSubject: false,
                        })

                        navi(
                          messageThreadPath.replace(':messageThreadUuid', id)
                        )
                      }}
                    />
                  )
                }
              </MessagesViewDataLoader>
            </Suspense>
          )}
        </ResidentAppCard>
      </ResidentAppView>
    </TitleController>
  )
}
